import React from "react";

export default function Api() {
  return (
    <div className="apidoc">
      <div className="container py-5">
        <h1 className="text-center mb-5">Apple API Documentation</h1>

        <div className="card mb-4">
          <div className="card-header">
            <h2 className="card-title">Introduction</h2>
          </div>
          <div className="card-body">
            <p className="card-text">
              Welcome to the Apple API documentation. This API allows you to
              retrieve, manage, and fix apple-related data.
            </p>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">
            <h2 className="card-title">Authentication</h2>
          </div>
          <div className="card-body">
            <p className="card-text">
              All API requests require an API key to be included in the header:
            </p>
            <pre className="bg-light p-2 rounded">
              <code>X-API-KEY: your_api_key_here</code>
            </pre>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">
            <h2 className="card-title">Url</h2>
          </div>
          <div className="card-body">
            <p className="card-text">httpa://api.api-apple.com/api/</p>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">
            <h2 className="card-title">Endpoints</h2>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="badge bg-primary">GET</span>
                    </td>
                    <td>/apples/all</td>
                    <td>Retrieve all apples</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="badge bg-primary">GET</span>
                    </td>
                    <td>/apple/{"{id}"}</td>
                    <td>Retrieve an apple by ID</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="badge bg-primary">GET</span>
                    </td>
                    <td>/apple/url/{"{url}"}</td>
                    <td>Retrieve an apple by URL</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="badge bg-success">POST</span>
                    </td>
                    <td>/apple/state/{"{id}"}</td>
                    <td>Change the state of an apple</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="badge bg-success">POST</span>
                    </td>
                    <td>/apple/fix</td>
                    <td>Fix defective apple accounts</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">
            <h2 className="card-title">Get All Apples</h2>
          </div>
          <div className="card-body">
            <h3>Endpoint: GET /apples/all</h3>
            <p className="card-text">
              This endpoint retrieves all apples associated with the
              authenticated API key.
            </p>
            <h4>Response:</h4>
            <p>Success (200 OK):</p>
            <pre className="bg-light p-2 rounded">
              <code>{`[
  {
    "id": 1,
    "email": "apple1@example.com",
    "url": "apple1",
    "state": true,
    ...
  },
  {
    "id": 2,
    "email": "apple2@example.com",
    "url": "apple2",
    "state": false,
    ...
  },
  ...
]`}</code>
            </pre>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">
            <h2 className="card-title">Get Apple by ID</h2>
          </div>
          <div className="card-body">
            <h3>Endpoint: GET /apple/{"{id}"}</h3>
            <p className="card-text">
              This endpoint retrieves a specific apple by its ID.
            </p>
            <h4>Response:</h4>
            <p>Success (200 OK):</p>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "id": 1,
  "email": "apple1@example.com",
  "url": "apple1",
  "state": true,
  ...
}`}</code>
            </pre>
            <p>Error (404 Not Found):</p>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "error": "Apple not found"
}`}</code>
            </pre>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">
            <h2 className="card-title">Get Apple by URL</h2>
          </div>
          <div className="card-body">
            <h3>Endpoint: GET /apple/url/{"{url}"}</h3>
            <p className="card-text">
              This endpoint retrieves a specific apple by its URL.
            </p>
            <h4>Response:</h4>
            <p>Success (200 OK):</p>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "id": 1,
  "email": "apple1@example.com",
  "url": "apple1",
  "state": true,
  ...
}`}</code>
            </pre>
            <p>Error (404 Not Found):</p>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "error": "Apple not found"
}`}</code>
            </pre>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">
            <h2 className="card-title">Change Apple State</h2>
          </div>
          <div className="card-body">
            <h3>Endpoint: POST /apple/state/{"{id}"}</h3>
            <p className="card-text">
              This endpoint allows you to change the state of a specific apple.
            </p>
            <h4>Request Body:</h4>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "state": true
}`}</code>
            </pre>
            <p>
              <strong>state:</strong> A boolean value (true or false)
              representing the new state of the apple.
            </p>
            <h4>Response:</h4>
            <p>Success (200 OK):</p>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "message": "Apple state updated successfully",
  "apple": {
    "id": 1,
    "email": "apple1@example.com",
    "url": "apple1",
    "state": true,
    "completeurl": "https://domain.com/apple1",
    ...
  }
}`}</code>
            </pre>
            <p>Error (404 Not Found):</p>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "error": "Apple not found or not owned by you"
}`}</code>
            </pre>
            <p>Error (422 Unprocessable Entity):</p>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "error": "Invalid input: State must be a boolean (true or false)"
}`}</code>
            </pre>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">
            <h2 className="card-title">Fix Apple Accounts</h2>
          </div>
          <div className="card-body">
            <h3>Endpoint: POST /apple/fix</h3>
            <p className="card-text">
              This endpoint allows you to fix defective apple accounts by
              replacing them with unsold ones.
            </p>
            <h4>Request Body:</h4>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "accounts": "123,124,125"
}`}</code>
            </pre>
            <p>
              <strong>accounts:</strong> A comma-separated string of account IDs
              to be fixed.
            </p>
            <h4>Response:</h4>
            <p>Success (200 OK):</p>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "message": "Apple accounts fixed successfully",
  "fixed_apples": 3
}`}</code>
            </pre>
            <p>Error (400 Bad Request):</p>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "error": "Please provide valid accounts ids"
}`}</code>
            </pre>
            <p>or</p>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "error": "Not enough unsold Apple accounts available. Please add more unsold accounts."
}`}</code>
            </pre>
            <p>Error (422 Unprocessable Entity):</p>
            <pre className="bg-light p-2 rounded">
              <code>{`{
  "error": "Invalid input: \"accounts\" field is required and must be a string like this \"123, 124, 125\""
}`}</code>
            </pre>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Example Usage</h2>
          </div>
          <div className="card-body">
            <p className="card-text mb-2">
              Here's an example of how to change an apple's state using
              JavaScript:
            </p>
            <pre className="bg-light p-3 rounded">
              <code>{`
fetch('https://api.api-apple.com/api/apple/state/123', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-KEY': 'your_api_key_here'
  },
  body: JSON.stringify({
    state: true
  })
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));
            `}</code>
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
